import React, { useState, useEffect, useRef } from "react";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Heading, HStack, Select } from "@chakra-ui/react";
import Exporting from 'highcharts/modules/exporting';
import ExportingData from 'highcharts/modules/export-data';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import { useParams } from "react-router-dom";
import { Text } from "@chakra-ui/react";
Exporting(Highcharts);
OfflineExporting(Highcharts);
ExportingData(Highcharts);

type ChartSeriesProps = {
  timePeriods: any;
  target: any;
  divisions: any;
};



/**
 * ChartSeries component renders a chart based on selected sub-target, action, and division.
 * It allows users to select a sub-target, action, and division to visualize corresponding data.
 * The chart is updated dynamically based on the selected options.
 *
 * @param {ChartSeriesProps} props - The props for the ChartSeries component.
 * @param {Array} props.timePeriods - The time periods for the x-axis of the chart.
 * @param {Object} props.target - The target object containing subtargets and actions.
 * @param {Array} props.divisions - The list of divisions to select from.
 *
 * @returns {JSX.Element} The ChartSeries component.
 *
 * @component
 *
 * @example
 * return (
 *   <ChartSeries
 *     timePeriods={timePeriods}
 *     target={target}
 *     divisions={divisions}
 *   />
 * )
 */
export default function ChartSeries({ timePeriods, target, divisions }: ChartSeriesProps) {
  const [actions, setActions] = useState([]);
  const [data, setData] = useState([]);
  const [dataTargetsValue, setDataTargetsValue] = useState([]);


  const params = useParams()
  const { targetIndex } = params;


  const [selectedSubTarget, setSelectedSubTarget] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedDivision, setSelectedSource] = useState("");

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const handleOnChangeSubTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const subTarget = target?.subtargets.find((st: any) => st._id === event.target.value);
    setActions(subTarget?.actions || []);
    if (event && event.target.value) {
      setSelectedSubTarget(event.target.value);
    } else {
      setSelectedSubTarget("");
    }
  };

  const handleOnChangeAction = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event && event.target.value) {
      setSelectedAction(event.target.value);
    } else {
      setSelectedAction("");
    }
  };

  const handleOnChangeSource = (event: React.ChangeEvent<HTMLSelectElement>) => {

    const newSelectedSource = event.target.value;
    if (event && event.target.value) {
      setSelectedSource(newSelectedSource);
    } else {
      setSelectedSource("");
    }


  }

  useEffect(() => {

    if (selectedAction && selectedSubTarget && selectedDivision) {
      const action = actions.find(act => act._id === selectedAction);
      let newData = []

      if (selectedDivision === "AGGR") {
        newData = action?.indicators ? action?.indicators?.map((indicator: any) => {
          let series = indicator.values ? Object.entries(indicator.values)
            .filter(([_, value]: any) => value !== '')
            .map(([key, value]: any) => [key, Object.entries(value).filter(([_, v]: any) => v !== '').reduce((acc, [_, v]: any) => acc + Number(v), 0)]) : [];

          return {
            type: 'line', data: series, name: 'Indicatore ' + indicator.index + " - " + indicator.title, lineWidth: 1, zIndex: 5, metadata: indicator.index
          };
        }) : [];
      } else {
        newData = action?.indicators ? action?.indicators?.map((indicator: any) => {
          let series = indicator.values ? Object.entries(indicator.values)
            .filter(([_, value]: any) => value !== '')
            .map(([key, value]: any) => [key, value[selectedDivision] == '' ? null : Number(value[selectedDivision])]) : [];
          return {
            type: 'line', data: series, name: 'Indicatore ' + indicator.index + " - " + indicator.title, lineWidth: 1, zIndex: 5, metadata: indicator.index
          };
        }) : [];
      }
      const cleanData = newData.filter((d: any) => d.data.length > 0);
      setData(cleanData);
    }
  }, [selectedAction, selectedSubTarget, selectedDivision]);

  const chartOptions: Highcharts.Options = {
    title: {
      text: 'Indicatori'
    },
    xAxis: {
      categories: timePeriods,
    },
    yAxis: {
      plotLines: dataTargetsValue,
    },
    series: data,
    exporting: {
      enabled: true,
      buttons:
      {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "separator",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
            "downloadCSV",
          ],
        },
      },
      menuItemDefinitions: {
        "viewFullscreen": {
          text: "Visualizza a schermo intero",
        },
        "printChart": {
          text: "Stampa",
        },
        "downloadPNG": {
          text: "Scarica PNG",
        },
        "downloadJPEG": {
          text: "Scarica JPEG",
        },
        "downloadPDF": {
          text: "Scarica PDF",
        },
        "downloadSVG": {
          text: "Scarica SVG",
        },
        "downloadCSV": {
          text: "Scarica CSV",
        }
      }
    },
    credits: {
      enabled: false
    },
  };


  // Update the chart when data changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.chart.update({ series: data });
      chartRef.current.chart.update({ yAxis: { plotLines: dataTargetsValue } });
    }
  }, [data, dataTargetsValue]);


  return (
    <>
      <HStack gap={2}>
        <Select
          aria-label="Seleziona un sotto obiettivo"
          placeholder={"Seleziona un sotto obiettivo"} onChange={handleOnChangeSubTarget} value={selectedSubTarget}>
          {target?.subtargets.map((subTarget: any, index: any) => (
            <option key={index} value={subTarget._id}>
              Sotto-obiettivo {subTarget.index} - {subTarget.title}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Seleziona un'azione"
          placeholder={"Seleziona un'azione"} onChange={handleOnChangeAction} value={selectedAction}>
          {actions.map((action, index) => (
            <option key={index} value={action._id}>
              Azione {action.index} - {action.title}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Seleziona un dipartimento/ufficio"
          placeholder={"Seleziona un dipartimento/ufficio"} onChange={handleOnChangeSource} value={selectedDivision}>
          <option key={0} value={"AGGR"}>Dato totale</option>
          {divisions.map((division: any, index: any) => (
            <option key={index} value={division.code}>
              {division.abbr} -  {division.title}
            </option>
          ))}
        </Select>
      </HStack>
      {
        data.length > 0 && selectedAction && selectedSubTarget && selectedDivision && (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartRef}
          />
        )
      }
      {
        data.length <= 0 && selectedAction && selectedSubTarget && selectedDivision &&
        <Heading as='h2' size='md' textAlign={"center"} mt={2}>
          Nessun dato inserito, grafico non disponibile.
        </Heading>
      }
    </>
  );
}
